import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {DhiraaguUserTemplateColumnIds} from "~/domainModels/workspaceSpecific/dhiraagu";
import {isDhiraaguWorkspace} from "~/domainModels/workspaceSpecific/dhiraagu";
import {isEmotoradWorkspace} from "~/domainModels/workspaceSpecific/emotorad";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import type {SearchConfiguration} from "~/components/ottopilotDataViewer/searchConfiguration";
import {
    PrivateInviteStatus,
    type PrivateInvite,
} from "@vesta/vos-types/modules/workspaceLevel/privateInvites/domain/PrivateInvite";

export type CreatePrivateInviteResponse = {
    inviteUrl: string;
};

export type GetPrivateInviteRequest = {
    workspaceId: Uuid;
    id: Uuid;
    inviteCode: string;
};

export type GetPrivateInviteUserTemplateItem = {
    id: Uuid;
    userId: Uuid;
    templateId: Uuid;
    parentId: Uuid | null;
    additionalColumnValues: Record<Uuid, unknown>;
    createdAt: string;
    createdBy: Uuid;
    updatedAt: string;
    updatedBy: Uuid;
    status: User["status"];
};

export type GetPrivateInviteOrganisationalUnitItem = {
    id: Uuid;
    templateId: Uuid;
    parentId: Uuid | null;
    additionalColumnValues: Record<Uuid, unknown>;
    createdAt: string;
    createdBy: Uuid;
    updatedAt: string;
    updatedBy: Uuid;
    status: Uuid;
};

export type GetPrivateInviteResponseData = PrivateInvite & {
    userTemplateItems: Array<GetPrivateInviteUserTemplateItem>;
    organisationalUnitItems: Array<GetPrivateInviteOrganisationalUnitItem>;
};

export function convertGetPrivateInviteUserTemplateItemsToUsers(items: Array<GetPrivateInviteUserTemplateItem>, workspaceId: Uuid): Array<User> {
    return items.map((item) => ({
        id: item.id,
        createdBy: item.createdBy,
        createdAt: item.createdAt as Iso8601DateTime,
        updatedBy: item.updatedBy,
        updatedAt: item.updatedAt as Iso8601DateTime,
        status: item.status as User["status"],
        templateId: item.templateId,
        parentId: item.parentId,
        additionalColumnValues: item.additionalColumnValues,
        userId: item.userId,
        workspaceId: workspaceId,
    }));
}

export function convertGetPrivateInviteOrganisationalUnitItemsToOrganizationalUnits(
    items: Array<GetPrivateInviteOrganisationalUnitItem>,
    workspaceId: Uuid,
): Array<OrganizationalUnit> {
    return items.map((item) => ({
        id: item.id,
        createdBy: item.createdBy,
        createdAt: item.createdAt as Iso8601DateTime,
        updatedBy: item.updatedBy,
        updatedAt: item.updatedAt as Iso8601DateTime,
        status: item.status as OrganizationalUnit["status"],
        templateId: item.templateId,
        parentId: item.parentId,
        additionalColumnValues: item.additionalColumnValues,
        workspaceId: workspaceId,
    }));
}

export function privateInviteStatusToDisplayName(status: PrivateInviteStatus): string {
    switch (status) {
        case PrivateInviteStatus.active: {
            return "Active";
        }
        case PrivateInviteStatus.changesRequested: {
            return "Changes requested";
        }
        case PrivateInviteStatus.draft: {
            return "Draft";
        }
        case PrivateInviteStatus.pending: {
            return "Pending review";
        }
        case PrivateInviteStatus.redeemed: {
            return "Redeemed";
        }
        case PrivateInviteStatus.rejected: {
            return "Rejected";
        }
        case PrivateInviteStatus.revoked: {
            return "Revoked";
        }
        case PrivateInviteStatus.expired: {
            return "Expired";
        }
        default: {
            console.error("Invalid privateInviteStatus", status);
            return "Unknown";
        }
    }
}
export type InvitedUser = Omit<User, "status" | "userId"> & {
    privateInviteStatus: PrivateInviteStatus;
    inviteCode: string;
    expiresAt: Iso8601DateTime;
    allowedEmail: PrivateInvite["allowedEmail"];
    allowedPhoneNumber: PrivateInvite["allowedPhoneNumber"];
    submittedAt: PrivateInvite["submittedAt"];
    submittedBy: PrivateInvite["submittedBy"];
    verificationRemark: PrivateInvite["verificationRemark"];
};
function detailsWithoutStringValues(details: Record<Uuid | string, string | Array<Uuid>>): Record<Uuid, unknown> {
    return Object.fromEntries(Object.entries(details).filter((entry) => entry[0] !== "parentId"));
}

function getInviteParentId(apiData: PrivateInvite): Uuid | null {
    return apiData.parentUserId;
}

export function convertInviteDataToUserTemplateItem(apiData: PrivateInvite): InvitedUser {
    const fixedDetails = detailsWithoutStringValues(apiData.fixedAdditionalColumnValues as Record<Uuid | string, string | Array<Uuid>>);
    const additionalColumnValues: Record<Uuid, unknown> = {
        ...(apiData.inviteFormData ?? {}),
        ...fixedDetails,
    };
    const parentId = getInviteParentId(apiData);
    return {
        inviteCode: apiData.inviteCode,
        expiresAt: apiData.expiresAt as Iso8601DateTime,
        id: apiData.id,
        createdBy: apiData.createdBy,
        createdAt: apiData.createdAt,
        updatedBy: apiData.updatedBy,
        updatedAt: apiData.updatedAt,
        templateId: apiData.userTemplateId,
        parentId: parentId,
        additionalColumnValues: additionalColumnValues,
        privateInviteStatus: apiData.status,
        allowedEmail: apiData.allowedEmail,
        allowedPhoneNumber: apiData.allowedPhoneNumber,
        submittedAt: apiData.submittedAt,
        submittedBy: apiData.submittedBy,
        verificationRemark: apiData.verificationRemark,
        workspaceId: apiData.workspaceId,
    };
}

export const getFixedAndPrefilledAdditionalColumns = (
    apiData: PrivateInvite,
): {
    fixedDetails: Array<Uuid>;
    prefilledDetails: Array<Uuid>;
} => {
    return {
        fixedDetails: Object.keys(apiData.fixedAdditionalColumnValues) as Array<Uuid>,
        prefilledDetails: Object.keys(apiData.inviteFormData ?? {}) as Array<Uuid>,
    };
};

function additionalColumnValueToInviteDetailValue(value: unknown): string | Array<Uuid> | null {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === "string") {
        return value;
    }
    if (Array.isArray(value)) {
        const asUuids = value.filter((v): v is Uuid => typeof v === "string");
        if (asUuids.length === value.length) {
            return asUuids;
        } else if (value.length === 0) {
            return null;
        }
        return null;
    }
    return null;
}

type UserWithAdditionalColumnValues = Pick<User, "id" | "templateId" | "additionalColumnValues" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy">;

function buildDetailsFromUser(user: UserWithAdditionalColumnValues, columnIds: Array<Uuid>): Record<Uuid, string | Array<Uuid>> {
    const result: Record<Uuid, string | Array<Uuid>> = {};
    for (const columnId of columnIds) {
        const value = user.additionalColumnValues[columnId];
        const normalized = additionalColumnValueToInviteDetailValue(value);
        if (normalized !== null) {
            result[columnId] = normalized;
        }
    }
    return result;
}

export type UserDerivedPrivateInviteData = Pick<
    PrivateInvite,
    "id" | "userTemplateId" | "inviteFormData" | "fixedAdditionalColumnValues" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy"
>;

export function transformUserToPrivateInvite(
    user: UserWithAdditionalColumnValues,
    prefilledDetailIds: Array<Uuid>,
    fixedDetailIds: Array<Uuid>,
): UserDerivedPrivateInviteData {
    return {
        id: user.id,
        userTemplateId: user.templateId,
        inviteFormData: buildDetailsFromUser(user, prefilledDetailIds),
        fixedAdditionalColumnValues: buildDetailsFromUser(user, fixedDetailIds),
        createdAt: user.createdAt,
        createdBy: user.createdBy,
        updatedAt: user.updatedAt,
        updatedBy: user.updatedBy,
    };
}

export type InviteIdToFixedAndPrefilledColumnIdsMap = Record<Uuid, {prefilledDetailIds: Array<Uuid>; fixedDetailIds: Array<Uuid>}>;

function workspaceUsesSemanticUserInviteColumnMap(workspace: {displayName: string}): boolean {
    if (isDhiraaguWorkspace(workspace) === true) {
        return true;
    }
    if (isEmotoradWorkspace(workspace) === true) {
        return true;
    }
    return false;
}

export function getInviteIdToFixedAndPrefilledColumnIdsMap(invites: Array<PrivateInvite>): InviteIdToFixedAndPrefilledColumnIdsMap {
    const map: InviteIdToFixedAndPrefilledColumnIdsMap = {};
    for (const invite of invites) {
        const {prefilledDetails, fixedDetails} = getFixedAndPrefilledAdditionalColumns(invite);
        map[invite.id] = {prefilledDetailIds: prefilledDetails, fixedDetailIds: fixedDetails};
    }
    return map;
}

/**
 * Returns fixed and prefilled column IDs for a Dhiraagu- or Emotorad-shaped role map based on form values.
 * Fixed: organizationalUnitColumnId (when role has it).
 * Prefilled: displayName, dob, gender, staffCode, verification*, etc.
 * parentId is handled separately as it is not a column ID.
 *
 * @returns null if template is not in userTemplateColumnMap
 */
export function getDhiraaguFixedAndPrefilledColumnIdsForUpdate(
    userTemplateColumnMap: Record<string, DhiraaguUserTemplateColumnIds>,
    templateId: Uuid,
    _formValues: Record<string, unknown>,
    workspaceDisplayName: string,
): {fixedDetailIds: Array<Uuid>; prefilledDetailIds: Array<Uuid>} | null {
    if (workspaceUsesSemanticUserInviteColumnMap({displayName: workspaceDisplayName}) !== true) {
        return null;
    }
    const config = userTemplateColumnMap[templateId];
    if (config === undefined) {
        return null;
    }
    const fixedDetailIds: Array<Uuid> = [];
    const prefilledDetailIds: Array<Uuid> = [];

    if (config.organizationalUnitColumnId !== null) {
        fixedDetailIds.push(config.organizationalUnitColumnId);
    }

    const fixedColumnIdSet = new Set(fixedDetailIds);
    for (const key of DHIRAAGU_SEMANTIC_KEYS) {
        if (key === "organizationalUnitColumnId") {
            continue;
        }
        const columnId = config[key];
        if (typeof columnId === "string" && fixedColumnIdSet.has(columnId) !== true) {
            prefilledDetailIds.push(columnId);
        }
    }

    return {fixedDetailIds, prefilledDetailIds};
}

const DHIRAAGU_SEMANTIC_KEYS = [
    "dob",
    "gender",
    "displayName",
    "staffCode",
    "verificationInvitedAt",
    "verificationInvitedBy",
    "verificationSubmittedOn",
    "verificationProcessedOn",
    "verificationProcessedBy",
    "verificationRejectReason",
    "organizationalUnitColumnId",
    "profileImageColumnId",
] as const;

/**
 * Maps additionalColumnValues from source template's column IDs to target template's column IDs
 * using userTemplateColumnMap. Applies when the workspace uses a Dhiraagu-shaped semantic map
 * (Dhiraagu, Emotorad) and both templates are in the map.
 *
 * @returns Mapped values, or null if mapping should not be applied (unsupported workspace or unknown template)
 */
export function mapAdditionalColumnValuesForRoleChangeDhiraagu(
    userTemplateColumnMap: Record<string, DhiraaguUserTemplateColumnIds>,
    sourceValues: Record<string, unknown>,
    sourceTemplateId: Uuid,
    targetTemplateId: Uuid,
    workspaceDisplayName: string,
): Record<string, unknown> | null {
    if (workspaceUsesSemanticUserInviteColumnMap({displayName: workspaceDisplayName}) !== true) {
        return null;
    }
    const sourceConfig = userTemplateColumnMap[sourceTemplateId];
    const targetConfig = userTemplateColumnMap[targetTemplateId];
    if (sourceConfig === undefined || targetConfig === undefined) {
        return null;
    }
    const result: Record<string, unknown> = {};
    for (const key of DHIRAAGU_SEMANTIC_KEYS) {
        if (key === "organizationalUnitColumnId") {
            const targetOuColumnId = targetConfig.organizationalUnitColumnId;
            if (targetOuColumnId === null) {
                continue;
            }
            const sourceOuColumnId = sourceConfig.organizationalUnitColumnId;
            if (sourceOuColumnId === null) {
                continue;
            }
            const value = sourceValues[sourceOuColumnId];
            if (value !== undefined && value !== null) {
                result[targetOuColumnId] = value;
            }
            continue;
        }
        const sourceColumnId = sourceConfig[key];
        const targetColumnId = targetConfig[key];
        if (sourceColumnId === null || targetColumnId === null) {
            continue;
        }
        const value = sourceValues[sourceColumnId];
        if (value !== undefined && value !== null) {
            result[targetColumnId] = value;
        }
    }
    return result;
}

export enum InviteSourceType {
    PUBLIC = "2769fbe2-39c9-49a3-a84a-9cbf8dddafc2",
    PRIVATE = "e4d77914-6f14-47ae-ad67-2db2f29313f9",
}

export type CreateJoinRequestRequest = {
    workspaceId: Uuid;
    sourceIdentifier: string;
    storeType: InviteSourceType;
    userId: Uuid;
    inviteCode: string;
    parentUserId?: Uuid;
    userTemplateId: Uuid;
    additionalColumnValues: Record<Uuid, string>;
};

// #region Copy-pasted from ottopilot--frontend
const PRIVATE_INVITE_PREFILLED_DETAILS_SEARCH_PREFIX = "inviteFormData.";
const PRIVATE_INVITE_FIXED_DETAILS_SEARCH_PREFIX = "fixedAdditionalColumnValues.";

const INVITE_SEARCHABLE_PREFILLED_TEMPLATE_COLUMN_KEYS: Array<keyof Pick<DhiraaguUserTemplateColumnIds, "displayName">> = ["displayName"];

export function resolvePrivateInviteApiSearchColumn(columnId: string): string {
    const trimmed = columnId.trim();
    if (trimmed === "") {
        return trimmed;
    }
    if (
        trimmed.startsWith(PRIVATE_INVITE_PREFILLED_DETAILS_SEARCH_PREFIX) === true ||
        trimmed.startsWith(PRIVATE_INVITE_FIXED_DETAILS_SEARCH_PREFIX) === true
    ) {
        return trimmed;
    }
    if (uuidZodValidator.safeParse(trimmed).success === true) {
        return `${PRIVATE_INVITE_PREFILLED_DETAILS_SEARCH_PREFIX}${trimmed}`;
    }
    return trimmed;
}

export function buildInviteModuleSearchColumns(args: {
    configuredSearchColumns: ReadonlyArray<string>;
    userTemplateColumnMap: Record<string, DhiraaguUserTemplateColumnIds> | null;
}): Array<string> {
    const columns = new Set<string>();
    for (const columnId of args.configuredSearchColumns) {
        columns.add(resolvePrivateInviteApiSearchColumn(columnId));
    }
    if (args.userTemplateColumnMap !== null) {
        for (const templateColumns of Object.values(args.userTemplateColumnMap)) {
            for (const fieldKey of INVITE_SEARCHABLE_PREFILLED_TEMPLATE_COLUMN_KEYS) {
                const columnUuid = templateColumns[fieldKey];
                columns.add(`${PRIVATE_INVITE_PREFILLED_DETAILS_SEARCH_PREFIX}${String(columnUuid)}`);
            }
        }
    }
    columns.add("inviteCode");
    return Array.from(columns);
}

export type PrivateInviteSearchFilterGroup = {
    logic: "OR";
    filters: Array<{
        column: string;
        operator: "contains";
        value: string;
    }>;
};

export function buildPrivateInviteSearchFilterGroup(searchConfiguration: SearchConfiguration | undefined): PrivateInviteSearchFilterGroup | null {
    if (searchConfiguration === undefined) {
        return null;
    }
    const searchColumns = searchConfiguration.searchColumns ?? [];
    const searchValue = searchConfiguration.searchValue.trim();
    if (searchColumns.length === 0 || searchValue === "") {
        return null;
    }

    return {
        logic: "OR",
        filters: searchColumns.map((columnId) => ({
            column: resolvePrivateInviteApiSearchColumn(columnId),
            operator: "contains",
            value: searchValue,
        })),
    };
}
// #endregion
